import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { BudgetTrackingType } from '@/types/budget-configuration.types';

interface BudgetTypeSelectorProps {
  value: BudgetTrackingType;
  onChange: (type: BudgetTrackingType) => void;
}

const budgetTypes = [
  {
    type: 'simple' as const,
    icon: '📊',
    label: 'Simple',
    description: 'Total hours per role',
    details: 'Track total budgeted hours for each role without monthly breakdown. Best for shorter projects or when monthly detail isn\'t needed.',
  },
  {
    type: 'monthly' as const,
    icon: '📅',
    label: 'Monthly',
    description: 'Monthly allocation',
    details: 'Track budgeted hours with monthly breakdown for each role. Ideal for longer projects requiring detailed timeline planning.',
  },
  {
    type: 'none' as const,
    icon: '⊘',
    label: 'None',
    description: 'No budget tracking',
    details: 'Disable budget tracking for all budget types. Projects will not track resource, sales, or cost budgets.',
  },
];

export function BudgetTypeSelector({ value, onChange }: BudgetTypeSelectorProps) {
  const selectedType = budgetTypes.find((t) => t.type === value);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {budgetTypes.map((type) => {
          const isSelected = value === type.type;
          return (
            <Card
              key={type.type}
              className={cn(
                'cursor-pointer transition-all hover:shadow-md',
                isSelected && 'ring-2 ring-primary shadow-md'
              )}
              onClick={() => onChange(type.type)}
            >
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="text-4xl">{type.icon}</div>
                  <div>
                    <h3 className="font-semibold text-lg">{type.label}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {type.description}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="w-full pt-2 border-t">
                      <span className="text-xs font-medium text-primary">
                        Selected
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedType && (
        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <div className="flex gap-2">
              <div className="text-2xl">{selectedType.icon}</div>
              <div className="flex-1">
                <h4 className="font-medium mb-1">
                  {selectedType.label} Budget Tracking
                </h4>
                <p className="text-sm text-muted-foreground">
                  {selectedType.details}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
